/**
 * A message's browser context (.plans/prd-browser-companion.md, REQ-F-023):
 *   - on the person's message, the page in front of them when they sent it
 *     from the browser companion's side panel: what "this page" means, for
 *     the agent and for background work on the message;
 *   - on the agent's reply, the page it read in their browser to answer,
 *     which the chat marks on the reply. A reply that read nothing has none.
 *
 * One contract for the three hops it crosses: the side panel hands it to the
 * framed chat (postMessage), the chat sends it with the message, the runtime
 * stores it (messages.context, sealed) and hands it back in the history.
 * Each hop parses it with `parseMessageContext`; none trusts the one before.
 *
 * Pure: no I/O.
 */
import { truncateChars } from "../text";

/** The page they were looking at. */
export interface MessagePage {
  /** Without its fragment (`#…`), which only says where they had scrolled. */
  readonly url: string;
  readonly title: string;
  /** The site's icon as the browser already had it, as a small PNG data URL
   *  (the extension reads Chrome's own favicon cache when it reads the page:
   *  no request to the site, then or when the chat shows it). Absent when
   *  there is none. */
  readonly icon?: string;
}

/**
 * What was visible of the page when the person sent their message, for an
 * agent whose page access is "with every message"
 * (.plans/prd-companion-page-with-message.md). It travels with the message
 * to the turn it was sent with and is never stored: the runtime keeps a
 * message's `page` and drops this before the row is written.
 */
export interface VisiblePage {
  /** The page the text was read from: it must be the message's page. */
  readonly url: string;
  readonly text: string;
  /** The person's text selection, when there is one. */
  readonly selection: string | null;
  /** Cut at VISIBLE_WITH_MESSAGE_MAX, or by the browser. */
  readonly truncated: boolean;
}

export interface MessageContext {
  /** Sent from the browser companion's side panel in Chrome. */
  readonly surface: "chrome";
  readonly page: MessagePage;
  /** In transit only, never on a stored row (VisiblePage). */
  readonly visible?: VisiblePage;
}

/** The most visible text a message carries; the whole page (40k) is for
 *  the tool. A viewport is normally far below this. */
export const VISIBLE_WITH_MESSAGE_MAX = 8_000;

/** The postMessage the side panel sends the chat it frames: the page the
 *  chat's next message is about, or null when it may not say (sharing off,
 *  paused, a page that is never shared). `withMessages` is true when the
 *  agent's page access is "with every message": the chat then asks for what
 *  is visible as each message is sent (PANEL_VISIBLE_REQUEST). */
export const PANEL_PAGE_MESSAGE = "squidler.companion.page";

/** The chat asking the panel for what is visible now, as a message is sent:
 *  `{type, id}`. Answered with PANEL_VISIBLE_MESSAGE `{type, id, visible}`,
 *  `visible` a VisiblePage or null (nothing could be read in time). */
export const PANEL_VISIBLE_REQUEST = "squidler.companion.visible.request";
export const PANEL_VISIBLE_MESSAGE = "squidler.companion.visible";

/** How long sending a message waits for what is visible before it goes
 *  without it (the agent can still read the tab with its tool). */
export const VISIBLE_WAIT_MS = 400;

/** The chat telling the panel it has loaded (and asking for the page), with
 *  the language it renders in: `{type, locale}`. */
export const CHAT_READY_MESSAGE = `${PANEL_PAGE_MESSAGE}.ready`;

/** The panel's language setting, sent to the chat it frames: `{type, locale}`.
 *  The chat switches the way the site's own language switcher does, so it is
 *  the person's language on the site too. */
export const PANEL_LANGUAGE_MESSAGE = "squidler.companion.language";

const MAX_URL = 2048;
const MAX_TITLE = 300;
/** A 32×32 PNG is a few kB; anything far bigger is not an icon. */
const MAX_ICON = 16_000;
const ICON_RE = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;

/** A page icon as the hops pass it: a small PNG data URL, never a link to
 *  fetch. Anything else is no icon. */
export const parseIcon = (v: unknown): string | null =>
  typeof v === "string" && v.length <= MAX_ICON && ICON_RE.test(v) ? v : null;

/** A page as the hops pass it, normalised, or null when it is not one. */
export const parseMessagePage = (v: unknown): MessagePage | null => {
  if (typeof v !== "object" || v === null) return null;
  const url = "url" in v && typeof v.url === "string" ? v.url : null;
  const title = "title" in v && typeof v.title === "string" ? v.title : "";
  if (url === null || url.length > MAX_URL) return null;
  const parsed = (() => { try { return new URL(url); } catch { return null; } })();
  if (parsed === null || (parsed.protocol !== "https:" && parsed.protocol !== "http:")) return null;
  parsed.hash = "";
  const cleanTitle = title.replace(/\s+/g, " ").trim();
  const icon = "icon" in v ? parseIcon(v.icon) : null;
  return {
    url: parsed.href,
    title: cleanTitle.length > MAX_TITLE ? `${truncateChars(cleanTitle, MAX_TITLE - 1)}…` : cleanTitle,
    ...(icon !== null && { icon }),
  };
};

/** What was visible, normalised and capped, or null when it is not one. Its
 *  text is the page author's: callers fence it, never trust it. */
export const parseVisiblePage = (v: unknown): VisiblePage | null => {
  if (typeof v !== "object" || v === null) return null;
  const url = "url" in v && typeof v.url === "string" && v.url.length <= MAX_URL ? v.url : null;
  const text = "text" in v && typeof v.text === "string" ? v.text : null;
  if (url === null || text === null) return null;
  const selection = "selection" in v && typeof v.selection === "string" && v.selection.trim() !== ""
    ? truncateChars(v.selection, VISIBLE_WITH_MESSAGE_MAX)
    : null;
  const cut = text.length > VISIBLE_WITH_MESSAGE_MAX;
  return {
    url,
    text: cut ? truncateChars(text, VISIBLE_WITH_MESSAGE_MAX) : text,
    selection,
    truncated: cut || ("truncated" in v && v.truncated === true),
  };
};

/**
 * A message's context as a hop receives it. What was visible is kept only
 * when it was read from the message's own page; anything else about it is
 * dropped, never the page.
 */
export const parseMessageContext = (v: unknown): MessageContext | null => {
  if (typeof v !== "object" || v === null || !("surface" in v) || v.surface !== "chrome") return null;
  const page = "page" in v ? parseMessagePage(v.page) : null;
  if (page === null) return null;
  const visible = "visible" in v ? parseVisiblePage(v.visible) : null;
  const visiblePage = visible === null ? null : parseMessagePage({ url: visible.url });
  return visible !== null && visiblePage?.url === page.url
    ? { surface: "chrome", page, visible }
    : { surface: "chrome", page };
};

/** The part of a context a row keeps: never what was visible. */
export const storedContext = (context: MessageContext): MessageContext => ({ surface: context.surface, page: context.page });

/** How a page is shown when it has no title: its host. */
export const pageLabel = (page: MessagePage): string => page.title || new URL(page.url).host;
